(() => {
  const HOLD_FADE = [
    { start: 0, end: 0.1, hold: 0 },
    { start: 0.1, end: 0.2, from: 0, to: 1 },
    { start: 0.2, end: 0.38, hold: 1 },
    { start: 0.38, end: 0.48, from: 1, to: 2 },
    { start: 0.48, end: 0.66, hold: 2 },
    { start: 0.66, end: 0.76, from: 2, to: 3 },
    { start: 0.76, end: 1, hold: 3 },
  ];

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

  const opacitiesFor = (progress) => {
    const values = [0, 0, 0, 0];

    for (const range of HOLD_FADE) {
      if (progress < range.start || progress > range.end) continue;

      if (Object.prototype.hasOwnProperty.call(range, 'hold')) {
        values[range.hold] = 1;
        return values;
      }

      const local = clamp((progress - range.start) / (range.end - range.start), 0, 1);
      const eased = easeInOut(local);
      values[range.from] = 1 - eased;
      values[range.to] = eased;
      return values;
    }

    values[3] = 1;
    return values;
  };

  const copyPrimaryOpacityFor = (progress) => {
    if (progress <= 0.1) return 1;
    if (progress < 0.2) return 1 - easeInOut(clamp((progress - 0.1) / 0.1, 0, 1));
    return 0;
  };

  const COPY_LINE_RANGES = [
    [0.14, 0.22],
    [0.21, 0.29],
    [0.28, 0.36],
    [0.35, 0.43],
    [0.42, 0.5],
    [0.49, 0.57],
    [0.56, 0.64],
    [0.63, 0.71],
  ];

  const copyLineStyleFor = (progress, start, end) => {
    if (progress <= start) return { opacity: 0, y: 6 };
    if (progress >= end) return { opacity: 1, y: 0 };
    const eased = easeInOut(clamp((progress - start) / (end - start), 0, 1));
    return { opacity: eased, y: 6 * (1 - eased) };
  };

  class LicohennaHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.licohenna-hero__track');
      this.slides = Array.from(this.querySelectorAll('.licohenna-hero__slide'));
      this.copyPrimary = this.querySelector('.licohenna-hero__copy--primary');
      this.copyLines = Array.from(this.querySelectorAll('.licohenna-hero__copy-line'));
      this.ticking = false;
      this.rafId = 0;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onScrollOrResize = () => {
        if (this.ticking) return;
        this.ticking = true;
        this.rafId = requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      };

      window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
      window.addEventListener('resize', this.onScrollOrResize, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollOrResize);
      window.removeEventListener('resize', this.onScrollOrResize);
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    progress() {
      if (!this.track) return 0;
      const scrollable = this.track.offsetHeight - window.innerHeight;
      if (scrollable <= 0) return 0;
      return clamp(-this.track.getBoundingClientRect().top / scrollable, 0, 1);
    }

    update() {
      const progress = this.progress();
      const values = opacitiesFor(progress);
      this.slides.forEach((slide, index) => {
        slide.style.opacity = String(values[index] ?? 0);
      });

      if (this.copyPrimary) this.copyPrimary.style.opacity = String(copyPrimaryOpacityFor(progress));

      this.copyLines.forEach((line, index) => {
        const range = COPY_LINE_RANGES[index] || COPY_LINE_RANGES[COPY_LINE_RANGES.length - 1];
        const style = copyLineStyleFor(progress, range[0], range[1]);
        line.style.opacity = String(style.opacity);
        line.style.transform = `translateY(${style.y}px)`;
      });
    }
  }

  if (!customElements.get('licohenna-hero')) {
    customElements.define('licohenna-hero', LicohennaHero);
  }
})();
